// src/pages/trading-portal/symbol-detail/SymbolDetailPage.js
// TradingView-style symbol detail page with 11 modular tabs

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Typography, Paper, Tabs, Tab, CircularProgress, Alert, IconButton, Tooltip,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Dashboard as DashboardIcon,
    AccountBalance as AccountBalanceIcon,
    Newspaper as NewspaperIcon,
    Description as DescriptionIcon,
    Forum as ForumIcon,
    ShowChart as ShowChartIcon,
    TrendingUp as TrendingUpIcon,
    CalendarMonth as CalendarMonthIcon,
    CandlestickChart as CandlestickChartIcon,
    Savings as SavingsIcon,
    PieChart as PieChartIcon,
    Refresh as RefreshIcon,
} from '@mui/icons-material';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';

// Tab components
import OverviewTab from './tabs/OverviewTab';
import FinancialTab from './tabs/FinancialTab';
import NewsTab from './tabs/NewsTab';
import DocumentsTab from './tabs/DocumentsTab';
import CommunityTab from './tabs/CommunityTab';
import TechnicalsTab from './tabs/TechnicalsTab';
import ForecastsTab from './tabs/ForecastsTab';
import SeasonalsTab from './tabs/SeasonalsTab';
import OptionsTab from './tabs/OptionsTab';
import BondsTab from './tabs/BondsTab';
import ETFsTab from './tabs/ETFsTab';

const TAB_CONFIG = [
    { label: 'Overview', icon: <DashboardIcon />, endpoint: 'overview', Component: OverviewTab },
    { label: 'Financial', icon: <AccountBalanceIcon />, endpoint: 'financial', Component: FinancialTab },
    { label: 'News', icon: <NewspaperIcon />, endpoint: 'news', Component: NewsTab },
    { label: 'Documents', icon: <DescriptionIcon />, endpoint: 'documents', Component: DocumentsTab },
    { label: 'Community', icon: <ForumIcon />, endpoint: 'community', Component: CommunityTab },
    { label: 'Technicals', icon: <ShowChartIcon />, endpoint: 'technicals', Component: TechnicalsTab },
    { label: 'Forecasts', icon: <TrendingUpIcon />, endpoint: 'forecasts', Component: ForecastsTab },
    { label: 'Seasonals', icon: <CalendarMonthIcon />, endpoint: 'seasonals', Component: SeasonalsTab },
    { label: 'Options', icon: <CandlestickChartIcon />, endpoint: 'options', Component: OptionsTab },
    { label: 'Bonds', icon: <SavingsIcon />, endpoint: 'bonds', Component: BondsTab },
    { label: 'ETFs', icon: <PieChartIcon />, endpoint: 'etfs', Component: ETFsTab },
];

const SymbolDetailPage = () => {
    const { symbolId } = useParams();
    const navigate = useNavigate();
    const [canView, setCanView] = useState(false);
    const [activeTab, setActiveTab] = useState(0);
    const [symbolData, setSymbolData] = useState(null);
    const [tabData, setTabData] = useState({});
    const [tabLoading, setTabLoading] = useState({});
    const [tabError, setTabError] = useState({});

    // Permission check
    useEffect(() => {
        const check = async () => {
            const view = await hasPermission('tr-watchlist', 'view');
            setCanView(view);
        };
        check();
    }, []);

    // Fetch tab data (lazy, only when tab is selected)
    const fetchTabData = useCallback(async (tabIndex) => {
        if (tabData[tabIndex] || tabLoading[tabIndex]) return;
        setTabLoading(prev => ({ ...prev, [tabIndex]: true }));
        setTabError(prev => ({ ...prev, [tabIndex]: null }));
        try {
            const endpoint = TAB_CONFIG[tabIndex].endpoint;
            const response = await axiosInstance.get(`/api/stock-trading/symbol-detail/${symbolId}/${endpoint}`);
            if (response.data.success) {
                setTabData(prev => ({ ...prev, [tabIndex]: response.data.data }));
                if (tabIndex === 0 && response.data.data.symbol) {
                    setSymbolData(response.data.data.symbol);
                }
            } else {
                setTabError(prev => ({ ...prev, [tabIndex]: response.data.message }));
            }
        } catch (err) {
            setTabError(prev => ({ ...prev, [tabIndex]: err.response?.data?.message || 'Failed to load data' }));
        } finally {
            setTabLoading(prev => ({ ...prev, [tabIndex]: false }));
        }
    }, [symbolId, tabData, tabLoading]);

    // Fetch overview on mount
    useEffect(() => {
        if (canView) fetchTabData(0);
    }, [canView, fetchTabData]);

    // Fetch when tab changes
    useEffect(() => {
        if (canView) fetchTabData(activeTab);
    }, [activeTab, canView, fetchTabData]);

    // Refresh current tab
    const handleRefresh = () => {
        setTabData(prev => { const copy = { ...prev }; delete copy[activeTab]; return copy; });
    };

    const formatPrice = (val) => val != null ? `$${Number(val).toFixed(2)}` : '-';

    if (!canView) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">You do not have permission to view this page.</Alert>
            </Box>
        );
    }

    // Active tab component
    const ActiveComponent = TAB_CONFIG[activeTab].Component;

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton onClick={() => navigate('/trading-portal/watchlist')}>
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                        <Typography variant="h4" fontWeight="bold">
                            {symbolData?.symbol || 'Loading...'}
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                            {symbolData?.company_name || ''}
                        </Typography>
                    </Box>
                    {symbolData && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 0.5 }}>
                            <Typography variant="h5" fontWeight="bold">
                                {formatPrice(symbolData.current_price)}
                            </Typography>
                            {symbolData.current_price && symbolData.previous_close && (() => {
                                const change = symbolData.current_price - symbolData.previous_close;
                                const changePct = symbolData.previous_close ? (change / symbolData.previous_close * 100) : 0;
                                const isPositive = change >= 0;
                                return (
                                    <Typography variant="h6" sx={{ color: isPositive ? '#22c55e' : '#ef4444' }}>
                                        {isPositive ? '+' : ''}{change.toFixed(2)} ({isPositive ? '+' : ''}{changePct.toFixed(2)}%)
                                    </Typography>
                                );
                            })()}
                            <Tooltip title="Refresh current tab">
                                <IconButton size="small" onClick={handleRefresh}>
                                    <RefreshIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>
                    )}
                </Box>
            </Box>

            {/* Tabs */}
            <Paper sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={(e, v) => setActiveTab(v)}
                    variant="scrollable"
                    scrollButtons="auto"
                    indicatorColor="primary"
                    textColor="primary"
                    sx={{ '& .MuiTab-root': { textTransform: 'none', minHeight: 48 } }}
                >
                    {TAB_CONFIG.map((tab, i) => (
                        <Tab key={i} label={tab.label} icon={tab.icon} iconPosition="start" />
                    ))}
                </Tabs>
            </Paper>

            {/* Active Tab Content */}
            <ActiveComponent
                data={tabData[activeTab]}
                loading={tabLoading[activeTab]}
                error={tabError[activeTab]}
                symbolId={symbolId}
            />
        </Box>
    );
};

export default SymbolDetailPage;
